const DB_NAME = 'SecureVaultDB';
const DB_VERSION = 2;
const ENTRIES_STORE = 'password_entries';
const METADATA_STORE = 'metadata';

export interface StoredEntry {
  id: string;
  encrypted_data: string;
  iv: string;
  category: string;
  created_at: number;
  updated_at: number;
  version: number;
  synced: number;
}

export interface SyncQueueItem {
  id: string;
  entry_id?: string;
  operation: 'create' | 'update' | 'delete';
  encrypted_data?: string;
  iv?: string;
  category?: string;
  timestamp: number;
}

class IndexedDBStorage {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  isSupported(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    if (!window.indexedDB) {
      return false;
    }
    return true;
  }

  async init(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    if (!this.isSupported()) {
      throw new Error('IndexedDB is not supported in this environment');
    }

    this.initPromise = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('IndexedDB initialization timeout'));
      }, 10000);
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        clearTimeout(timeout);
        reject(request.error);
      };
      request.onsuccess = () => {
        clearTimeout(timeout);
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = (event.target as IDBOpenDBRequest).transaction!;

        if (!db.objectStoreNames.contains(ENTRIES_STORE)) {
          const entriesStore = db.createObjectStore(ENTRIES_STORE, { keyPath: 'id' });
          entriesStore.createIndex('category', 'category', { unique: false });
          entriesStore.createIndex('updated_at', 'updated_at', { unique: false });
          entriesStore.createIndex('synced', 'synced', { unique: false });
        } else if (event.oldVersion < 2) {
          const entriesStore = transaction.objectStore(ENTRIES_STORE);

          if (entriesStore.indexNames.contains('synced')) {
            entriesStore.deleteIndex('synced');
          }

          entriesStore.createIndex('synced', 'synced', { unique: false });

          const cursorRequest = entriesStore.openCursor();
          cursorRequest.onsuccess = (e) => {
            const cursor = (e.target as IDBRequest).result;
            if (cursor) {
              const entry = cursor.value;
              if (typeof entry.synced === 'boolean') {
                entry.synced = entry.synced ? 1 : 0;
                cursor.update(entry);
              }
              cursor.continue();
            }
          };
        }

        if (!db.objectStoreNames.contains(METADATA_STORE)) {
          db.createObjectStore(METADATA_STORE, { keyPath: 'key' });
        }
      };
    });

    return this.initPromise;
  }

  private getStore(storeName: string, mode: IDBTransactionMode): IDBObjectStore {
    if (!this.db) {
      throw new Error('Database not initialized. Call init() first.');
    }
    const transaction = this.db.transaction(storeName, mode);
    return transaction.objectStore(storeName);
  }

  isInitialized(): boolean {
    return this.db !== null;
  }

  async saveEntry(entry: StoredEntry): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore(ENTRIES_STORE, 'readwrite');
      const request = store.put(entry);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getEntry(id: string): Promise<StoredEntry | null> {
    return new Promise((resolve, reject) => {
      const store = this.getStore(ENTRIES_STORE, 'readonly');
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllEntries(): Promise<StoredEntry[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore(ENTRIES_STORE, 'readonly');
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteEntry(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore(ENTRIES_STORE, 'readwrite');
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getUnsyncedEntries(): Promise<StoredEntry[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore(ENTRIES_STORE, 'readonly');
      const index = store.index('synced');
      const request = index.getAll(IDBKeyRange.only(0));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async setMetadata(key: string, value: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore(METADATA_STORE, 'readwrite');
      const request = store.put({ key, value });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getMetadata(key: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const store = this.getStore(METADATA_STORE, 'readonly');
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result?.value || null);
      request.onerror = () => reject(request.error);
    });
  }

  async clearAll(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction(
        [ENTRIES_STORE, METADATA_STORE],
        'readwrite'
      );

      const entriesStore = transaction.objectStore(ENTRIES_STORE);
      const metadataStore = transaction.objectStore(METADATA_STORE);

      entriesStore.clear();
      metadataStore.clear();

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async searchEntries(query: string): Promise<StoredEntry[]> {
    const allEntries = await this.getAllEntries();
    const lowerQuery = query.toLowerCase();

    return allEntries.filter(entry =>
      entry.category.toLowerCase().includes(lowerQuery)
    );
  }
}

export const localDB = new IndexedDBStorage();
